/**
 * @file StockLevelCells.tsx
 * @description Ячейки строки остатка: партия, срок, адрес хранения, галочка
 * отбора и поле числа этикеток (phase19.24 этап 1, шаг 4; плоская таблица
 * партий — phase19.31, SC59).
 *
 * Галочка размещения — не просто `Checkbox`: визуально это 20px, а нажимать
 * её кладовщик будет пальцем, поэтому она завёрнута в `<label>` на 48px
 * (`<lg`, компактный список; 56px у кладовщика — по плотности темы) / 44px
 * (`lg`, таблица). Увеличивать сам квадрат нельзя — 56px заливки в строке
 * таблицы читается как кнопка, а не как отметка.
 *
 * Зона клика — именно обёртка, а не псевдоэлемент `after`: невидимая накладка
 * вылезала за строку, перекрывала соседнюю и не измерялась как тач-цель.
 * `<label>` без `htmlFor` кликает скрытый `input` внутри base-ui-чекбокса —
 * двойного переключения нет, сам чекбокс гасит click через `preventDefault`.
 *
 * Текстовые форматы строки лежат в `stock-level-format.ts`.
 */

import { stockExpiryView, stockLotShort } from "./stock-level-format";
import { PLACEMENT_TEXT } from "./stock-placement-texts";
import { isPlaceable } from "./stock-row-actions";
import { Checkbox } from "../../ui/checkbox";
import { formatSkuName, type StockLevelDto } from "../../../lib/api-wms";
import { LABEL_MAX_QTY_PER_LINE } from "../../../lib/label-sheets";
import { cn } from "@/lib/utils";

/** Тон текста по сроку — реестром (SC59: «не указан» янтарным). */
const EXPIRY_TONE_CLASS = {
  default: "text-on-surface",
  warning: "text-warning",
  muted: "text-on-surface-variant",
} as const;

/** Адрес хранения партии; «без места» — янтарным (то, что ищет отбор дашборда). */
export function StockBinCell({ level }: { level: StockLevelDto }) {
  if (level.lot?.bin) {
    return <span className="font-mono text-xs">{level.lot.bin.code}</span>;
  }
  return (
    <span className={level.lot ? "text-warning" : "text-on-surface-variant"}>
      {level.lot ? "без места" : "—"}
    </span>
  );
}

/** Номер партии одной строкой в коротком виде; полный — в подсказке. */
export function StockLotCell({ level }: { level: StockLevelDto }) {
  if (!level.lot) return <span className="text-on-surface-variant">без партии</span>;
  const full = level.lot.batchNumber;
  return (
    <span className="block truncate font-mono text-xs" title={full} aria-label={`Партия ${full}`}>
      {stockLotShort(level)}
    </span>
  );
}

/** «Годен до»: дата, «не указан» янтарным или «—». */
export function StockExpiryCell({ level }: { level: StockLevelDto }) {
  const view = stockExpiryView(level);
  return <span className={cn("tabular-nums", EXPIRY_TONE_CLASS[view.tone])}>{view.text}</span>;
}

/** Поле «Печать» таблицы: число этикеток у отмеченной строки. */
export function StockLabelQtyInput({
  level,
  value,
  onChange,
}: {
  level: StockLevelDto;
  value: number;
  onChange: (qty: number) => void;
}) {
  return (
    <input
      type="text"
      inputMode="numeric"
      value={String(value)}
      onChange={(e) => onChange(Math.min(Number(e.target.value.replace(/\D/g, "") || "0"), LABEL_MAX_QTY_PER_LINE))}
      aria-label={`Этикеток: ${formatSkuName(level.sku)}`}
      className="h-9 w-full rounded-lg bg-surface-lowest px-2 text-right text-sm tabular-nums [box-shadow:inset_0_0_0_1px_var(--field-border,var(--border))] focus:outline-none focus:ring-2 focus:ring-ring"
    />
  );
}

interface SelectCellProps {
  level: StockLevelDto;
  checked: boolean;
  onToggle: (level: StockLevelDto) => void;
  /** phase19.22 — правило отбора задаёт панель; нет — правило размещения. */
  state?: { can: boolean; hint: string };
}

/**
 * Галочка строки. Беспартийный остаток размещать нечем — вместо галочки
 * подпись-объяснение, иначе кладовщик щёлкает по недоступной строке и не
 * понимает, почему счётчик не растёт.
 */
export function StockSelectCell({ level, checked, onToggle, state }: SelectCellProps) {
  const can = state ? state.can : isPlaceable(level);
  if (!can) {
    return (
      <span className="block max-w-[88px] text-xs text-on-surface-variant">
        {state ? state.hint : level.lot ? "уже размещена" : PLACEMENT_TEXT.noLotHint}
      </span>
    );
  }
  return (
    // Зона клика: высота кнопки темы до 1024 (48, у кладовщика 56), 44 в таблице.
    <label className="inline-flex size-[var(--control-h,3rem)] shrink-0 cursor-pointer items-center justify-center lg:size-11">
      <Checkbox
        checked={checked}
        onCheckedChange={() => onToggle(level)}
        aria-label={`${PLACEMENT_TEXT.selectRowLabel}: ${level.lot?.batchNumber ?? ""}`}
        className="size-5"
      />
    </label>
  );
}
